import { type ReactElement, useMemo } from 'react';
import { EmptyState } from '@vello/web-theme';
import { type AdminMarketChoice, adminScreenMarket, adminScreenMarketChoices } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';

/**
 * The market a per-market Admin screen reads or writes (owner, 2026-10-12): it is ALWAYS the header's scope — the
 * scoped city, or the only market the operator's grants reach. No screen draws a second market selector: to see
 * another market the operator switches the scope at the top. `market` is `undefined` while the scope is a country (or
 * global) with several markets; the screen then shows `<ScopeMarketNotice />` instead of its list or form.
 */
export function useScopeMarket(): { readonly market: string | undefined; readonly choices: readonly AdminMarketChoice[] } {
  const { scope, markets } = useAdmin();
  const choices = useMemo(() => adminScreenMarketChoices(scope, markets), [scope, markets]);
  const market = useMemo(() => adminScreenMarket(scope, choices, undefined), [scope, choices]);
  return { market, choices };
}

/** What a per-market screen shows while the scope names no single market: pick a city in the scope at the top. */
export function ScopeMarketNotice({ choices }: { readonly choices: readonly AdminMarketChoice[] }): ReactElement {
  const { t } = useT();
  const { scope } = useAdmin();
  // D854 (L6b-009): "no market this account can reach" is a statement about the account, so it is made only when a country / state
  // narrowed the scope to nothing. At Global the guidance is the same as at a country - pick a city in the scope at the top - never a
  // sentence that reads as if the operator had no market.
  const accountHasNone = choices.length === 0 && scope.current.country !== undefined;
  return accountHasNone ? <EmptyState title={t('w6.noMarkets')} /> : <EmptyState title={t('w6.needsMarket.title')} detail={t('w6.needsMarket.detail')} />;
}
